/**
 * Infrastructure Intelligence — Construction Progress  (Step 8b of the 8-step intelligence loop)
 *
 * Track cut-fill volumetrics and earthwork progress for active construction sites.
 * Compares baseline DEM against latest drone survey to compute net earthwork volumes,
 * percentage completion, and schedule compliance indicators.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  HardHat, RefreshCw, TrendingUp, TrendingDown, BarChart2,
  ChevronDown, ChevronUp, Mountain,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProgressMission {
  id: string;
  name: string;
  status: string;
  created_at: string;
  updated_at: string;
  metadata?: {
    ai_analysis_status?: string;
    vertical?: string;
    asset_name?: string;
    step7_status?: string;
    step8_status?: string;
    cut_volume_m3?: number;
    fill_volume_m3?: number;
    earthwork_delta_m3?: number;
    completion_percent?: number;
    diagnosis?: string;
    [key: string]: any;
  };
}

// ─── Volume badge helper ──────────────────────────────────────────────────────

function VolumeBadge({ value, label }: { value: number | undefined | null; label: string }) {
  if (value == null) return <span className="text-gray-400">—</span>;
  return (
    <div className="text-center">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-lg font-bold text-gray-900 dark:text-white">{Number(value).toLocaleString()}</p>
      <p className="text-xs text-gray-400">m³</p>
    </div>
  );
}

function CompletionBar({ pct }: { pct: number | undefined | null }) {
  if (pct == null) return null;
  const clamped = Math.min(100, Math.max(0, pct));
  const color = clamped < 25 ? 'bg-red-500' : clamped < 60 ? 'bg-amber-400' : clamped < 90 ? 'bg-blue-500' : 'bg-green-500';
  return (
    <div className="mt-2">
      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
        <span>Completion</span><span>{clamped.toFixed(0)}%</span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${clamped}%` }} />
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ConstructionProgressPage: React.FC = () => {
  const navigate = useNavigate();
  const [missions, setMissions]     = useState<ProgressMission[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions?mission_type=infrastructure_survey&limit=100');
      const all: ProgressMission[] = res.data?.missions ?? [];
      // Show all infrastructure missions that have completed AI analysis — progress tracking use case
      setMissions(
        all.filter(
          (m) =>
            m.metadata?.vertical === 'infrastructure' &&
            m.metadata?.ai_analysis_status === 'complete'
        )
      );
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load progress data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Summary stats
  const totalCut  = missions.reduce((s, m) => s + (m.metadata?.cut_volume_m3 ?? 0), 0);
  const totalFill = missions.reduce((s, m) => s + (m.metadata?.fill_volume_m3 ?? 0), 0);
  const netDelta  = totalCut - totalFill;

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <HardHat className="w-7 h-7 text-blue-500" /> Construction Progress
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 8b — Earthwork cut-fill volumetrics and construction schedule compliance
          </p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Portfolio summary stats ── */}
      {missions.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card padding="sm">
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Cut Volume</p>
            <p className="text-2xl font-bold mt-1 text-orange-700 dark:text-orange-400">
              {totalCut > 0 ? `${(totalCut / 1000).toFixed(1)}k` : '0'} m³
            </p>
          </Card>
          <Card padding="sm">
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Fill Volume</p>
            <p className="text-2xl font-bold mt-1 text-blue-700 dark:text-blue-400">
              {totalFill > 0 ? `${(totalFill / 1000).toFixed(1)}k` : '0'} m³
            </p>
          </Card>
          <Card padding="sm">
            <p className="text-xs text-gray-500 dark:text-gray-400">Net Earthwork Δ</p>
            <p className={`text-2xl font-bold mt-1 ${netDelta > 0 ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
              {netDelta > 0 ? '+' : ''}{netDelta !== 0 ? `${(netDelta / 1000).toFixed(1)}k` : '0'} m³
            </p>
          </Card>
        </div>
      )}

      {/* ── Mission progress list ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {missions.length} survey{missions.length !== 1 ? 's' : ''} with volumetric data
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : missions.length === 0 ? (
          <div className="text-center py-14">
            <Mountain className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No volumetric data available</p>
            <p className="text-sm text-gray-400 mt-1">Complete infrastructure drone surveys and AI analysis first</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {missions.map((m) => {
              const isExpanded = expandedId === m.id;
              const delta      = m.metadata?.earthwork_delta_m3;
              const hasDelta   = delta != null;

              return (
                <div key={m.id}>
                  <button
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : m.id)}
                  >
                    {hasDelta && delta! > 5000
                      ? <TrendingUp className="w-4 h-4 flex-shrink-0 text-red-500" />
                      : hasDelta && delta! < -5000
                      ? <TrendingDown className="w-4 h-4 flex-shrink-0 text-green-500" />
                      : <BarChart2 className="w-4 h-4 flex-shrink-0 text-blue-400" />
                    }
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{m.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {m.metadata?.asset_name ?? '—'} · {new Date(m.updated_at).toLocaleDateString('en-GB')}
                      </p>
                    </div>
                    {hasDelta && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium font-mono ${delta! > 0 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'}`}>
                        {delta! > 0 ? '+' : ''}{Number(delta).toLocaleString()} m³
                      </span>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700">
                      <div className="grid grid-cols-3 gap-6 mb-4">
                        <VolumeBadge value={m.metadata?.cut_volume_m3} label="Cut Volume (m³)" />
                        <VolumeBadge value={m.metadata?.fill_volume_m3} label="Fill Volume (m³)" />
                        <VolumeBadge value={m.metadata?.earthwork_delta_m3} label="Net Delta (m³)" />
                      </div>
                      <CompletionBar pct={m.metadata?.completion_percent} />
                      {m.metadata?.diagnosis && (
                        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 italic">{m.metadata.diagnosis}</p>
                      )}
                      <div className="mt-4">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/missions/${m.id}`)}>
                          Open Mission
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ConstructionProgressPage;

// Made with Bob
